import OrderItem from '../components/OrderItem';
import React, { useEffect, useState, useCallback } from 'react';
import FundraisersService from "../services/FundraisersService";
import OrdersService from "../services/OrdersService";
import { useParams, useNavigate } from "react-router-dom";
import DonationTiers from '../components/DonationTiers';
import { selectDonationTier, setDonationAmount, startCustomDonation } from '../utils/donationState';
import GoogleDriveVideo from '../components/GoogleDriveVideo';
import SelectedBundleDisplay from '../components/SelectedBundleDisplay';
import BundleTiers from '../components/BundleTiers';
import BundlePreviewModal from '../components/BundlePreviewModal';
import StudentInfoForm from '../components/StudentInfoForm';
import TeamHeader from '../components/TeamHeader';
import OrderSummary from '../components/OrderSummary';
import ProductSelection from '../components/ProductSelection';
import { usePurchaseState } from '../hooks/usePurchaseState';

// Utility imports
import { 
    findMatchingBundle, 
    checkBundleStatus,
    analyzeOrderForBundleAndExtras
} from '../utils/bundleCalculations';
import { calculateTotal, roundToHundredth } from '../utils/orderCalculations';
import { validateOrderSubmission } from '../utils/orderValidation';

export default function Order() {

    // Cash check can only be entered by the student's device and that takes them here.
    const { fundraiserId } = useParams();
    const { orderId } = useParams();
    const { fdid } = useParams();
    const { cashcheck } = useParams();
    const { contactGuid } = useParams();
    const { socialGuid } = useParams();
    const navigate = useNavigate();
    
    // Main order data (complex, frequently updated)
    const [order, setOrder] = useState();
    
    // Student information
    const [studentInfo, setStudentInfo] = useState({
        firstName: "",
        lastName: ""
    });
    
    // Fundraiser configuration (rarely changes together)
    const [fundraiserConfig, setFundraiserConfig] = useState({
        isDonate: false,
        hasShipping: false,
        isActive: false,
        fundraiserDeviceId: null
    });
    
    // Donation state (always used together)
    const [donationState, setDonationState] = useState({
        isCustom: false,
        customAmount: null,
        selectedTier: null
    });
    
    // Purchase state management via custom hook
    const {
        bundleState,
        additionalQuantities,
        selectBundle,
        resetToCustomPurchase,
        removeBundle,
        loadBundleOptions,
        handleBundleSelection: hookHandleBundleSelection,
        handleOptionChange: hookHandleOptionChange,
        handleRemoveBundle: hookHandleRemoveBundle,
        updateBundleStatus,
        getBundleQuantity,
        analyzeBundleForOrder,
        setAdditionalQuantities,
        updateBundleState,
        isCustomPurchase,
        selectedBundle,
        bundleOptions,
        bundleItems,
        bundleStatus
    } = usePurchaseState();
    
    // Modal state
    const [modalState, setModalState] = useState({
        showPreview: false,
        previewBundle: null
    });
    
    // Tracking state
    const [trackingState, setTrackingState] = useState({
        hasRecordedVisit: false
    });



    // Helper functions moved to utils/bundleCalculations.js

    const handleDonationTierSelect = (amount) => {
        setDonationState(prev => selectDonationTier(prev, amount));
        setOrder(prev => setDonationAmount(prev, amount));
    };

    // findMatchingBundle moved to utils/bundleCalculations.js

    // checkBundleStatus moved to utils/bundleCalculations.js

    const autoSelectMatchingBundle = useCallback(() => {
        // Only auto-select if no bundle is currently selected AND not in custom purchase mode
        if (selectedBundle || isCustomPurchase) {
            return;
        }

        const matchingBundle = findMatchingBundle(bundleOptions, order);
        
        if (matchingBundle) {
            // Build bundle items for display
            const bundleItemsData = [];
            const actualAllocations = new Map();
            
            matchingBundle.optionBundleEntries.forEach(bundleEntry => {
                // Set actual allocation for auto-selected bundle
                actualAllocations.set(bundleEntry.itemsOptionId, bundleEntry.quantity);
                
                order.fundraiserOrderItems.forEach(item => {
                    const matchingOption = item.fundraiserOrderItemOptions.find(
                        option => option.itemOptionId === bundleEntry.itemsOptionId
                    );
                    if (matchingOption) {
                        bundleItemsData.push({
                            name: matchingOption.name,
                            price: matchingOption.price,
                            quantity: bundleEntry.quantity
                        });
                    }
                });
            });
            
            selectBundle(matchingBundle, bundleItemsData, 'complete');
            // Also update the actual allocations for the auto-selected bundle
            updateBundleState({ actualAllocations: actualAllocations });
        }
    }, [selectedBundle, isCustomPurchase, bundleOptions, order, selectBundle, updateBundleState]);



    const handlePreviewBundle = (bundle) => {
        setModalState({
            showPreview: true,
            previewBundle: bundle
        });
    };

    const handleConfirmBundle = (bundle) => {
        handleBundleSelection(bundle);
        setModalState(prev => ({ ...prev, showPreview: false }));
    };

    const handleOptionChange = (bundleEntry, newOptionId) => {
        if (!order || !order.fundraiserOrderItems) return;
        

        
        // Update the selectedBundle state to persist the change (immutably)
        const updatedBundle = {
            ...selectedBundle,
            optionBundleEntries: selectedBundle.optionBundleEntries.map(entry =>
                entry === bundleEntry
                    ? { ...entry, itemsOptionId: parseInt(newOptionId) }
                    : entry
            )
        };
        updateBundleState({ selected: updatedBundle });
        
        // Accumulate quantities for each option used in the bundle
        const optionQuantities = new Map();
        updatedBundle.optionBundleEntries.forEach(entry => {
            const currentQty = optionQuantities.get(entry.itemsOptionId) || 0;
            optionQuantities.set(entry.itemsOptionId, currentQty + entry.quantity);
        });
        
        // Update order with new quantities (immutably)
        setOrder(prevOrder => {
            const updatedOrder = {
                ...prevOrder,
                fundraiserOrderItems: prevOrder.fundraiserOrderItems.map(item => ({
                    ...item,
                    fundraiserOrderItemOptions: item.fundraiserOrderItemOptions.map(option => {
                        const newQty = optionQuantities.get(option.itemOptionId) || 0;
                        if (newQty !== option.qty) {

                        }
                        return { ...option, qty: newQty };
                    })
                }))
            };
            const newTotal = calculateTotal(updatedOrder);
            return { ...updatedOrder, orderTotal: newTotal };
        });
        
        // Refresh bundle items display with the updated information and actual allocations
        const bundleItemsData = [];
        const actualAllocations = new Map();
        
        updatedBundle.optionBundleEntries.forEach(entry => {
            // Set actual allocation for this bundle entry
            const currentAllocation = actualAllocations.get(entry.itemsOptionId) || 0;
            actualAllocations.set(entry.itemsOptionId, currentAllocation + entry.quantity);
            
            order.fundraiserOrderItems.forEach(item => {
                const matchingOption = item.fundraiserOrderItemOptions.find(
                    option => option.itemOptionId === entry.itemsOptionId
                );
                if (matchingOption) {
                    bundleItemsData.push({
                        name: matchingOption.name,
                        price: matchingOption.price,
                        quantity: entry.quantity
                    });
                }
            });
        });
        
        updateBundleState({ 
            items: bundleItemsData,
            actualAllocations: actualAllocations 
        });
        updateBundleStatus();
    };

    const handleBundleSelection = (bundle, preserveQuantities = false) => {
        if (!bundle) {
            // Custom Purchase selected
            resetToCustomPurchase();
            
            // Reset additional quantities
            setAdditionalQuantities(new Map());
            
            // Only reset quantities if not preserving them (for order edits)
            if (!preserveQuantities && order && order.fundraiserOrderItems) {
                setOrder(prevOrder => {
                    const updatedOrder = {
                        ...prevOrder,
                        fundraiserOrderItems: prevOrder.fundraiserOrderItems.map(item => ({
                            ...item,
                            fundraiserOrderItemOptions: item.fundraiserOrderItemOptions.map(option => ({
                                ...option,
                                qty: 0
                            }))
                        }))
                    };
                    const newTotal = calculateTotal(updatedOrder);
                    return { ...updatedOrder, orderTotal: newTotal };
                });
            }
            return;
        }

        // Bundle selected - disable auto-detection going forward for manual selections
        updateBundleState({
            selected: bundle,
            isCustomPurchase: false,
            disableBundleDetection: true // Prevent auto-detection after manual selection
        });
        
        // Reset additional quantities when new bundle selected (unless preserving for edits)
        if (!preserveQuantities) {
            setAdditionalQuantities(new Map());
        }
        
        // Apply bundle quantities to the options (immutably)
        if (order && order.fundraiserOrderItems) {
            // Build quantities map from bundle
            const bundleQuantities = new Map();
            bundle.optionBundleEntries.forEach(bundleEntry => {
                bundleQuantities.set(bundleEntry.itemsOptionId, bundleEntry.quantity);
            });
            
            // Update order with bundle quantities (preserveQuantities means keep higher of current or bundle)
            setOrder(prevOrder => {
                const updatedOrder = {
                    ...prevOrder,
                    fundraiserOrderItems: prevOrder.fundraiserOrderItems.map(item => ({
                        ...item,
                        fundraiserOrderItemOptions: item.fundraiserOrderItemOptions.map(option => {
                            const bundleQty = bundleQuantities.get(option.itemOptionId) || 0;
                            // If preserveQuantities is true (order edit), keep higher of current qty or bundle qty
                            const newQty = preserveQuantities ? Math.max(option.qty, bundleQty) : bundleQty;
                            return { ...option, qty: newQty };
                        })
                    }))
                };
                const newTotal = calculateTotal(updatedOrder);
                return { ...updatedOrder, orderTotal: newTotal };
            });
            
            // Build bundle items display data and actual allocations
            const bundleItemsData = [];
            const actualAllocations = new Map();
            
            bundle.optionBundleEntries.forEach(bundleEntry => {
                // Set actual allocation for this bundle entry
                actualAllocations.set(bundleEntry.itemsOptionId, bundleEntry.quantity);
                
                order.fundraiserOrderItems.forEach(item => {
                    const matchingOption = item.fundraiserOrderItemOptions.find(
                        option => option.itemOptionId === bundleEntry.itemsOptionId
                    );
                    if (matchingOption) {
                        bundleItemsData.push({
                            name: matchingOption.name,
                            price: matchingOption.price,
                            quantity: bundleEntry.quantity
                        });
                    }
                });
            });
            
            updateBundleState({
                items: bundleItemsData,
                status: 'complete',
                actualAllocations: actualAllocations
            });
        }
    };

    useEffect(() => {
        if (fdid) {
            // Check visit tracking first - only for social media or direct device links (not contact links)
            if (fdid.length === 36 && !trackingState.hasRecordedVisit && !contactGuid) {
                const visitedGuids = JSON.parse(sessionStorage.getItem('visitedFundraiserGuids') || '[]');
                
                if (!visitedGuids.includes(fdid)) {
                    visitedGuids.push(fdid);
                    sessionStorage.setItem('visitedFundraiserGuids', JSON.stringify(visitedGuids));
                    
                    OrdersService.incrementPageVisits(fdid)
                        .then(() => {
                            setTrackingState(prev => ({ ...prev, hasRecordedVisit: true }));
                        })
                        .catch(error => {
                            console.error('Error recording page visit:', error);
                        });
                }
            }

            // Then proceed with getting fundraiser details
            FundraisersService.getFundraiserDetailsForDevice(fdid).then((x) => {
                let processedOrder = x;
                if (cashcheck) {
                    // Filter out items with itemId 43, 44, 45, 46 (coffee, cookie dough, FRM, beef sticks)
                    processedOrder = {
                        ...x,
                        fundraiserOrderItems: x.fundraiserOrderItems
                            .filter(item => item.itemId !== 43 && item.itemId !== 44 && item.itemId !== 45 && item.itemId !== 46)
                            .map(item => ({
                                ...item,
                                fundraiserOrderItemOptions: item.fundraiserOrderItemOptions.filter(option => option.itemOptionTypeId === 0)
                            }))
                    };
                }
                setOrder(processedOrder);
                setFundraiserConfig({
                    isDonate: x.isDonate,
                    hasShipping: x.hasShipping,
                    isActive: x.isActive,
                    fundraiserDeviceId: x.FundraiserDeviceId
                });
                setStudentInfo({
                    firstName: x.studentFirstName || '',
                    lastName: x.studentLastName || ''
                });

            });
        }

        if (contactGuid) {
            // Track student contact engagement for contact-specific links
            if (contactGuid.length === 36 && !trackingState.hasRecordedVisit) {
                const visitedContactGuids = JSON.parse(sessionStorage.getItem('visitedContactGuids') || '[]');
                
                if (!visitedContactGuids.includes(contactGuid)) {
                    visitedContactGuids.push(contactGuid);
                    sessionStorage.setItem('visitedContactGuids', JSON.stringify(visitedContactGuids));
                    
                    OrdersService.incrementStudentContactEngagement(contactGuid)
                        .then(() => {
                            setTrackingState(prev => ({ ...prev, hasRecordedVisit: true }));
                        })
                        .catch(error => {
                            console.error('Error recording student contact engagement:', error);
                        });
                }
            }

            // Handle student contact GUID route
            FundraisersService.getFundraiserDetailsForStudentContact(contactGuid).then((x) => {
                setOrder(x);
                setFundraiserConfig({
                    isDonate: x.isDonate,
                    hasShipping: x.hasShipping,
                    isActive: x.isActive,
                    fundraiserDeviceId: x.FundraiserDeviceId
                });
                setStudentInfo({
                    firstName: x.studentFirstName || '',
                    lastName: x.studentLastName || ''
                });

            });
        }

        if (socialGuid) {
            // Track social media visits
            if (socialGuid.length === 36 && !trackingState.hasRecordedVisit) {
                const visitedSocialGuids = JSON.parse(sessionStorage.getItem('visitedSocialGuids') || '[]');
                
                if (!visitedSocialGuids.includes(socialGuid)) {
                    visitedSocialGuids.push(socialGuid);
                    sessionStorage.setItem('visitedSocialGuids', JSON.stringify(visitedSocialGuids));
                    
                    OrdersService.incrementSocialMediaEngagement(socialGuid)
                        .then(() => {
                            setTrackingState(prev => ({ ...prev, hasRecordedVisit: true }));
                        })
                        .catch(error => {
                            console.error('Error recording social media engagement:', error);
                        });
                }
            }

            // Handle social media GUID route
            FundraisersService.getFundraiserDetailsForSocialMedia(socialGuid).then((x) => {
                setOrder(x);
                setFundraiserConfig({
                    isDonate: x.isDonate,
                    hasShipping: x.hasShipping,
                    isActive: x.isActive,
                    fundraiserDeviceId: x.FundraiserDeviceId
                });
                setStudentInfo({
                    firstName: x.studentFirstName || '',
                    lastName: x.studentLastName || ''
                });

            });
        }

        if (fundraiserId) {
            FundraisersService.getFundraiserDetails(fundraiserId).then(x => {
                setOrder(x);

                setFundraiserConfig({
                    isDonate: x.isDonate,
                    hasShipping: x.hasShipping,
                    isActive: x.isActive,
                    fundraiserDeviceId: null
                });
            });
        }
        if (orderId) {
            OrdersService.getFundraiserOrder(orderId).then((x) => {
                setOrder(x);
                setFundraiserConfig(prev => ({
                    ...prev,
                    isDonate: x.isDonate,
                    isActive: x.isActive
                }));
                setStudentInfo({
                    firstName: x.studentFirstName || '',
                    lastName: x.studentLastName || ''
                });
                setDonationState(prev => ({
                    ...prev,
                    isCustom: !!x.donation,
                    customAmount: x.donation || null,
                    selectedTier: null // Will be set by donation tiers if it matches a preset
                }));
            });
        }
    }, [fdid, contactGuid, socialGuid, fundraiserId, orderId, cashcheck, trackingState.hasRecordedVisit]);

    // Fetch option bundles when order is loaded
    useEffect(() => {
        if (order && order.fundraiserOrderItems && order.fundraiserOrderItems.length > 0) {
            const firstItemId = order.fundraiserOrderItems[0].itemId;
            FundraisersService.getOptionBundlesForItem(firstItemId)
                .then(bundles => {
                    loadBundleOptions(bundles);
                })
                .catch(error => {
                    console.error('Error fetching option bundles:', error);
                    // If bundle fetch fails, default to custom purchase mode
                    loadBundleOptions([]);
                });
        }
    }, [order]);

    // Handle bundle auto-selection when bundles are loaded OR when bundle fetch is complete
    useEffect(() => {
        // Only run if we have order data, haven't selected a bundle, and auto-detection is enabled
        if (order && order.fundraiserOrderItems && !selectedBundle && !bundleState.disableBundleDetection) {
            // If we have bundles available, try bundle logic
            if (bundleOptions.length > 0 && !isCustomPurchase) {
            // Check if this is an order edit (has existing quantities)
            const hasExistingQuantities = order.fundraiserOrderItems.some(item => 
                item.fundraiserOrderItemOptions.some(option => option.qty > 0)
            );
            
            if (hasExistingQuantities) {
                // This is an order edit - analyze for bundle + extras

                
                const analysis = analyzeBundleForOrder(order);
                
                switch (analysis.type) {
                    case 'bundle_only':
                    case 'bundle_with_extras':

                        
                        // Create a modified bundle that reflects the user's actual option choices
                        const modifiedBundle = {
                            ...analysis.bundle,
                            optionBundleEntries: analysis.actualBundleEntries
                        };
                        
                        // Create actual allocations map from the analysis
                        const actualAllocations = new Map();
                        analysis.actualBundleEntries.forEach(entry => {
                            actualAllocations.set(entry.itemsOptionId, entry.quantity);
                        });
                        
                        // Set bundle state with user's actual selections preserved
                        updateBundleState({
                            selected: modifiedBundle,
                            isCustomPurchase: false,
                            disableBundleDetection: true,
                            status: 'complete',
                            actualAllocations: actualAllocations
                        });
                        
                        // Set extra quantities if any
                        if (analysis.type === 'bundle_with_extras') {
                            setAdditionalQuantities(analysis.extraQuantities);
                        }
                        
                        // Build bundle items display data using actual user selections
                        const bundleItemsData = [];
                        analysis.actualBundleEntries.forEach(entry => {
                            order.fundraiserOrderItems.forEach(item => {
                                const matchingOption = item.fundraiserOrderItemOptions.find(
                                    option => option.itemOptionId === entry.itemsOptionId
                                );
                                if (matchingOption) {
                                    // Use the allocated bundle quantity for this specific entry
                                    bundleItemsData.push({
                                        name: matchingOption.name,
                                        price: matchingOption.price,
                                        quantity: entry.quantity // Use allocated bundle quantity
                                    });
                                }
                            });
                        });
                        updateBundleState({ items: bundleItemsData });
                        break;
                        
                    case 'custom':

                        hookHandleBundleSelection(null, true, order, setOrder);
                        break;
                }
            } else {
                // New order - try to auto-select a matching bundle (original behavior)
                // ONLY if not in custom purchase mode
                if (!isCustomPurchase) {
                    autoSelectMatchingBundle();
                }
            }
            }
            // If we're in custom purchase mode or no bundles, nothing more to do
            // The component will automatically show the individual items
        }
    }, [bundleOptions, order, selectedBundle, bundleState.disableBundleDetection, isCustomPurchase, autoSelectMatchingBundle, analyzeBundleForOrder, updateBundleState, hookHandleBundleSelection]); // Remove autoSelectMatchingBundle from dependencies to prevent re-runs
    
    // calculateTotal moved to utils/orderCalculations.js
    

    function incCount(optionToUpdate) {
        setOrder(prevOrder => {
            const updatedOrder = {
                ...prevOrder,
                fundraiserOrderItems: prevOrder.fundraiserOrderItems.map(item => ({
                    ...item,
                    fundraiserOrderItemOptions: item.fundraiserOrderItemOptions.map(option =>
                        option === optionToUpdate 
                            ? { ...option, qty: option.qty + 1 }
                            : option
                    )
                }))
            };
            const newTotal = calculateTotal(updatedOrder);
            return { ...updatedOrder, orderTotal: newTotal };
        });
        updateBundleStatus();
    }

    function decCount(optionToUpdate) {
        if (optionToUpdate.qty > 0) {
            // Check if this would go below bundle minimum
            const bundleQuantity = getBundleQuantity(optionToUpdate.itemOptionId);
            const newQuantity = optionToUpdate.qty - 1;
            
            if (selectedBundle && bundleQuantity > 0 && newQuantity < bundleQuantity) {
                alert("This is the minimum quantity required for your selected bundle. You can add more items or choose 'Custom Purchase' to customize individual quantities.");
                return; // Don't allow the decrement
            }
            
            setOrder(prevOrder => {
                const updatedOrder = {
                    ...prevOrder,
                    fundraiserOrderItems: prevOrder.fundraiserOrderItems.map(item => ({
                        ...item,
                        fundraiserOrderItemOptions: item.fundraiserOrderItemOptions.map(option =>
                            option === optionToUpdate 
                                ? { ...option, qty: option.qty - 1 }
                                : option
                        )
                    }))
                };
                const newTotal = calculateTotal(updatedOrder);
                return { ...updatedOrder, orderTotal: newTotal };
            });
            updateBundleStatus();
        }
    }

    // roundToHundredth moved to utils/orderCalculations.js

    const handleSubmit = (e) => {
        e.preventDefault();

        const validation = validateOrderSubmission(studentInfo, order);
        if (!validation.isValid) {
            alert(validation.message);
            return;
        }

        // Create order object with all required fields (immutably)
        const orderToSubmit = {
            ...order,
            studentFirstName: studentInfo.firstName,
            studentLastName: studentInfo.lastName,
            FundraiserDeviceId: fundraiserConfig.fundraiserDeviceId,
            ...(contactGuid && { contactGuid }),
            ...(socialGuid && { socialGuid })
        };
        
        if (cashcheck) {
            OrdersService.CreateOrder(orderToSubmit).then(x => navigate("/checkoutcashcheck/" + x.data + "/" + cashcheck));
        }
        else {
            OrdersService.CreateOrder(orderToSubmit).then(x => navigate("/checkout/" + x.data));
        }
    };
    return (
        <div>
            {order && fundraiserConfig.isActive === false && (
                <>
                    <div style={{
                        backgroundColor: order.teamPrimaryColorHex || '#f8f9fa',
                        color: order.teamSecondaryColorHex || '#333',
                        padding: '2rem',
                        borderRadius: '10px',
                        marginBottom: '2rem',
                        textAlign: 'center'
                    }}>
                        {order.teamPhotoUrl && (
                            <img 
                                src={`https://fundraising-u-production.s3.amazonaws.com/${order.teamPhotoUrl}`}
                                alt={`${order.teamName} logo`}
                                style={{
                                    maxHeight: '150px',
                                    marginBottom: '1rem',
                                    objectFit: 'contain'
                                }}
                            />
                        )}
                        <h1>{order.teamName}</h1>
                        {order.schoolName && <h3>{order.schoolName}</h3>}
                        <p className="mt-3">This fundraiser is no longer active.</p>
                        <p>Thank you for your support.</p>
                    </div>
                    
                    {/* Show video even for inactive fundraisers */}
                    {order.videoUrl && (
                        <div style={{
                            backgroundColor: '#fff',
                            padding: '2rem',
                            borderRadius: '10px',
                            marginBottom: '2rem',
                            border: '1px solid #dee2e6'
                        }}>
                            <h3 style={{ 
                                color: order.teamPrimaryColorHex || '#333', 
                                marginBottom: '1.5rem',
                                textAlign: 'center' 
                            }}>
                                Team Message
                            </h3>
                            <GoogleDriveVideo 
                                videoUrl={order.videoUrl} 
                                teamName={order.teamName}
                            />
                        </div>
                    )}
                </>
            )}

            {order && fundraiserConfig.isActive === true && (
                <>
                    <TeamHeader 
                        teamName={order.teamName}
                        schoolName={order.schoolName}
                        teamPhotoUrl={order.teamPhotoUrl}
                        teamBackgroundPhotoUrl={order.teamBackgroundPhotoUrl}
                        useTeamBackgroundPhoto={order.useTeamBackgroundPhoto}
                        teamPrimaryColorHex={order.teamPrimaryColorHex}
                        videoUrl={order.videoUrl}
                    />

                    {/* Student Information Section */}
                    <StudentInfoForm 
                        studentInfo={studentInfo}
                        onStudentInfoChange={setStudentInfo}
                        teamPrimaryColor={order.teamPrimaryColorHex}
                    />
                        
                        <form onSubmit={handleSubmit} className="needs-validation" noValidate>

                            {/* Donation Section */}
                            {fundraiserConfig.isDonate && (
                                <div style={{
                                    backgroundColor: '#fff',
                                    padding: '2rem',
                                    borderRadius: '10px',
                                    marginBottom: '2rem',
                                    border: '1px solid #dee2e6'
                                }}>
                                    <h3 style={{ color: order.teamPrimaryColorHex || '#0e456c', marginBottom: '1.5rem' }}>
                                        Make Your Impact
                                    </h3>
                                    
                                    <DonationTiers 
                                        selectedAmount={donationState.selectedTier}
                                        onSelectAmount={handleDonationTierSelect}
                                    />
                                    
                                    <div className="mt-4 text-center">
                                        <button 
                                            type="button"
                                            className="btn btn-link"
                                            onClick={() => {
                                                setDonationState(prev => startCustomDonation(prev));
                                                setOrder(prev => setDonationAmount(prev, 0));
                                            }}
                                        >
                                            Or enter a custom amount
                                            </button>
                                        
                                        {donationState.isCustom && (
                                            <div className="mt-3">
                                                <input 
                                                    type="number" 
                                                    className="form-control mx-auto" 
                                                    style={{ maxWidth: '300px' }}
                                                    placeholder="Enter amount (minimum $35)"
                                                    value={donationState.customAmount || ''}
                                                    min="35"
                                                    onChange={(e) => {
                                                        const value = parseFloat(e.target.value) || 0;
                                                        setDonationState(prev => ({ ...prev, customAmount: value }));
                                                        setOrder(prevOrder => {
                                                            const updatedOrder = { ...prevOrder, donation: value };
                                                            const newTotal = calculateTotal(updatedOrder);
                                                            return { ...updatedOrder, orderTotal: newTotal };
                                                        });
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <ProductSelection
                                bundleOptions={bundleOptions}
                                selectedBundle={selectedBundle}
                                bundleItems={bundleItems}
                                bundleStatus={bundleStatus}
                                isCustomPurchase={isCustomPurchase}
                                additionalQuantities={additionalQuantities}
                                order={order}
                                teamPrimaryColor={order.teamPrimaryColorHex}
                                onSelectBundle={(bundle) => hookHandleBundleSelection(bundle, false, order, setOrder)}
                                onPreviewBundle={handlePreviewBundle}
                                onRemoveBundle={() => hookHandleRemoveBundle(order, setOrder)}
                                onOptionChange={(bundleEntry, newOptionId) => hookHandleOptionChange(bundleEntry, newOptionId, order, setOrder)}
                                onIncCount={incCount}
                                onDecCount={decCount}
                                getBundleQuantity={getBundleQuantity}
                            />

                            <OrderSummary 
                                orderTotal={order ? order.orderTotal : 0}
                                teamPrimaryColorHex={order.teamPrimaryColorHex}
                                hasShipping={fundraiserConfig.hasShipping}
                            />
                        </form>
                </>
            )}

             {/* Bundle Preview Modal */}
             <BundlePreviewModal
                 show={modalState.showPreview}
                 bundle={modalState.previewBundle}
                 onHide={() => setModalState(prev => ({ ...prev, showPreview: false }))}
                 onConfirm={handleConfirmBundle}
                 teamPrimaryColor={order?.teamPrimaryColorHex}
             />
         </div>
     );
 }
